import { memo, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronRight, type LucideIcon } from 'lucide-react';

export interface SettingsNavigationCardProps {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description: string;
  readonly active: boolean;
  readonly badge?: ReactNode;
  readonly disabled?: boolean;
  readonly panelId: string;
  readonly tabId: string;
  readonly tabIndex?: number;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  readonly tabRef?: (element: HTMLButtonElement | null) => void;
  readonly onClick: () => void;
  /** Compact exclusive-accordion: expose expand/collapse to AT. */
  readonly accordionMode?: boolean;
  readonly expanded?: boolean;
}

export const SettingsNavigationCard = memo(
  ({
    icon: Icon,
    title,
    description,
    active,
    badge,
    disabled = false,
    panelId,
    tabId,
    tabIndex,
    onKeyDown,
    tabRef,
    onClick,
    accordionMode = false,
    expanded = false,
  }: SettingsNavigationCardProps): JSX.Element => {
    const rootClass = [
      'asp-settings-nav-card',
      active ? 'asp-settings-nav-card--active' : '',
      disabled ? 'asp-settings-nav-card--disabled' : '',
      accordionMode && expanded ? 'asp-settings-nav-card--expanded' : '',
    ]
      .filter((part) => part.length > 0)
      .join(' ');

    const chevronClass = [
      'asp-settings-nav-card__chevron',
      'h-4',
      'w-4',
      'shrink-0',
      'text-[var(--color-an-text-muted)]',
      'stroke-[1.75]',
      accordionMode && expanded ? 'asp-settings-nav-card__chevron--expanded' : '',
    ]
      .filter((part) => part.length > 0)
      .join(' ');

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (disabled) {
        return;
      }

      onKeyDown?.(event);
      if (event.defaultPrevented) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    };

    return (
      <button
        id={tabId}
        type="button"
        role="tab"
        className={rootClass}
        aria-selected={active}
        aria-controls={accordionMode && !expanded ? undefined : panelId}
        aria-expanded={accordionMode ? expanded : undefined}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        tabIndex={tabIndex}
        ref={tabRef}
        onClick={disabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
      >
        <span className="asp-settings-icon-box asp-settings-icon-box--nav" aria-hidden="true">
          <Icon className="h-5 w-5 stroke-[1.75]" />
        </span>
        <span className="asp-settings-nav-card__text">
          <span className="asp-settings-nav-card__title">{title}</span>
          <span className="asp-settings-nav-card__description">{description}</span>
        </span>
        {badge != null && badge !== '' ? <span className="asp-settings-nav-card__badge">{badge}</span> : null}
        <ChevronRight className={chevronClass} aria-hidden="true" />
      </button>
    );
  },
);

SettingsNavigationCard.displayName = 'SettingsNavigationCard';
